import React, { FC, memo, useCallback, useEffect, useMemo } from 'react';
import { Form } from 'antd';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { AppLanguage } from 'app/config/i18Config/types';
import { useAppSelector } from 'app/config/storeConfig/hooks';
import { getGlobalSettings } from 'app/appState';
import { showNotification } from 'app/providers/NotificationsProvider';
import { Button } from 'shared/ui/Button';
import { Modal } from 'shared/ui/Modal';
import { Select } from 'shared/ui/Select';
import { TextArea } from 'shared/ui/TextArea';
import { WarningPing } from 'shared/ui/WarningPing';
import { languageToLabelMap } from 'shared/utils/constants';
import { Contract, SendContractClosureNotificationParams, useSendContractClosureNotificationMutation } from 'entities/Contract';

interface SendContractClosureNotificationModalProps {
  contract: Nullable<Contract>;
  onClose: () => void;
}

export const SendContractClosureNotificationModal: FC<SendContractClosureNotificationModalProps> = memo((props) => {
  const { contract, onClose } = props;

  const { t } = useAppTranslation(['user', 'common']);

  const [form] = Form.useForm<SendContractClosureNotificationParams>();

  const globalSettings = useAppSelector(getGlobalSettings);

  const [sendContractClosureNotification, { isLoading }] = useSendContractClosureNotificationMutation();

  const isOpen = Boolean(contract);

  const languages = useMemo(
    () =>
      globalSettings?.availableLanguages
        .filter((lang: string): lang is AppLanguage => Object.values(AppLanguage).includes(lang as AppLanguage))
        .map((language: AppLanguage) => ({
          label: languageToLabelMap[language],
          value: language,
        })),
    [globalSettings?.availableLanguages],
  );

  useEffect(() => {
    if (contract) {
      const defaultLanguage = Object.values(AppLanguage).includes(contract.user.language as AppLanguage)
        ? (contract.user.language as AppLanguage)
        : undefined;

      form.setFieldsValue({
        language: defaultLanguage,
        reason: '',
      });
    } else {
      form.resetFields();
    }
  }, [form, contract]);

  const handleSendContractClosureNotification = useCallback(
    async (data: SendContractClosureNotificationParams): Promise<void> => {
      try {
        if (!contract) {
          return;
        }

        await sendContractClosureNotification({ ...data, contractId: contract.contractId }).unwrap();

        showNotification('info', t('Success', { ns: 'common' }), t('Contract closure warning has been sent successfully'));

        onClose();
      } catch (error: CustomAny) {
        showNotification('error', t('Error', { ns: 'common' }), t('Error when sending contract closure notification'));
        console.log('error', error);
      }
    },
    [contract, onClose, sendContractClosureNotification, t],
  );

  return (
    <Modal className="max-w-[600px]" isOpen={isOpen} onClose={onClose}>
      <Form form={form} layout="vertical" onFinish={handleSendContractClosureNotification} className="flex flex-col">
        <div className="mb-2">
          <WarningPing type="warning" />
        </div>

        <div className="font-semibold text-xl mb-3">{t('Send contract closure notification')}</div>

        <div className="text-primaryLight font-normal mb-6">
          {t('Are you sure you want to send the client a contract closure notification?')}
        </div>

        <div className="text-primaryLight font-normal mb-4">{t('Select the language in which the mail will be sent')}</div>

        <Form.Item
          name="language"
          label={t('Current user language')}
          rules={[{ required: true, message: t('Please select mail language!') }]}
        >
          <Select placeholder={t('Select offer language')} options={languages || []} bordered />
        </Form.Item>

        <Form.Item label={t('Reason')} name="reason" rules={[{ required: true, message: `${t('Please, enter reason')}!` }]}>
          <TextArea placeholder={t('Enter reason')} rows={4} bordered />
        </Form.Item>

        <div className="flex justify-end gap-3 mt-4">
          <Button theme="secondary" onClick={onClose}>
            {t('Cancel', { ns: 'common' })}
          </Button>

          <Button type="submit" isLoading={isLoading}>
            {t('Confirm', { ns: 'common' })}
          </Button>
        </div>
      </Form>
    </Modal>
  );
});
